import _ from 'lodash';
import React, { useCallback } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { connectWithScope } from '#packages/apilib';

import HiddenElementThumbnail from './hiddenElementThumbnail';
import { HiddenElementsScope } from '../../hiddenElementsScope';
import { mapDispatchToProps } from './hiddenElementsSectionMapper';

import type { HiddenElement } from '../../hiddenElementsTypes';
import type { HiddenElementsSectionDispatchProps } from './hiddenElementsSectionMapper';

import styles from './hiddenElementsSection.scss';

interface HiddenElementsSectionOwnProps {
  elements: Array<HiddenElement>;
  title?: string;
  sectionType?: 'masterPage' | 'currentPage';
}

interface HiddenElementsSectionProps
  extends HiddenElementsSectionOwnProps,
    HiddenElementsSectionDispatchProps {}

const HiddenElementsSection: React.FC<HiddenElementsSectionProps> = ({
  title,
  elements,
  unhideComponent,
  previewComponentOnStage,
  stopPreviewComponentOnStage,
}) => {
  const [isPreviewDisabled, setIsPreviewDisabled] = React.useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedPreviewComponentOnStage = useCallback(
    _.debounce(previewComponentOnStage, 500),
    [previewComponentOnStage],
  );

  const onThumbnailMouseEnter = useCallback(
    async (element: HiddenElement) => {
      if (isPreviewDisabled) {
        return;
      }
      await debouncedPreviewComponentOnStage(element);
    },
    [isPreviewDisabled, debouncedPreviewComponentOnStage],
  );

  const onThumbnailMouseLeave = useCallback(
    (element: HiddenElement) => {
      debouncedPreviewComponentOnStage.cancel();

      if (!isPreviewDisabled) {
        stopPreviewComponentOnStage(element);
      }
    },
    [
      isPreviewDisabled,
      stopPreviewComponentOnStage,
      debouncedPreviewComponentOnStage,
    ],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onThumbnailClick = useCallback(
    async (element: HiddenElement) => {
      debouncedPreviewComponentOnStage.cancel();

      // some comps are being added to stage really
      // long so user can preview another more lightweight
      // component before the first one is added.
      // To avoid confusing user we block the preview
      // until the comp is added
      setIsPreviewDisabled(true);
      await unhideComponent(element);
      setIsPreviewDisabled(false);
    },
    [unhideComponent, debouncedPreviewComponentOnStage],
  );

  return (
    <div className={styles.container}>
      {title && <div className={styles.header}>{title}</div>}
      <div className={styles.content}>
        <TransitionGroup>
          {elements.map((element) => (
            <CSSTransition
              key={element.compId}
              timeout={500}
              classNames={{
                exit: styles.thumbnailRemoveExit,
                exitActive: styles.thumbnailRemoveExitActive,
              }}
            >
              <HiddenElementThumbnail
                element={element}
                key={element.compId}
                onMouseEnter={onThumbnailMouseEnter}
                onMouseLeave={onThumbnailMouseLeave}
                onClick={onThumbnailClick}
              />
            </CSSTransition>
          ))}
        </TransitionGroup>
      </div>
    </div>
  );
};

export default connectWithScope(
  () => HiddenElementsScope,
  HiddenElementsSection,
  null,
  mapDispatchToProps,
);
